import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import type { Team } from "@/lib/api/types";
import { useSetComplianceThreshold, useTeamMembers } from "@/hooks/queries/use-teams";
import { useOnboardingState } from "@/hooks/queries/use-onboarding";
import { useAuthStore } from "@/stores/auth-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** The score used when a project has no threshold of its own (matches the API). */
export const DEFAULT_COMPLIANCE_THRESHOLD = 70;
const MIN = 50;
const MAX = 100;

function errorText(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : "Something went wrong. Try again.";
}

/** Returns the whole number in the 50-100 range, or null when the text isn't one. */
export function parseThreshold(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n >= MIN && n <= MAX ? n : null;
}

/**
 * Project setting: the lowest compliance score an epic can have before a run
 * can start (ENG-380). Empty means the default (70). Project admins,
 * organization admins and platform admins can change it; everyone else sees it
 * read-only.
 */
export function ComplianceThresholdCard({ team }: { team: Team }) {
  const me = useAuthStore((s) => s.user);
  const { data: onboarding } = useOnboardingState();
  const { data: members } = useTeamMembers(team.id);
  const setThreshold = useSetComplianceThreshold(team.id);

  const saved: number | null =
    setThreshold.data !== undefined
      ? (setThreshold.data.data.complianceThreshold ?? null)
      : (team.complianceThreshold ?? null);
  const [draft, setDraft] = useState(saved === null ? "" : String(saved));
  useEffect(() => {
    setDraft(saved === null ? "" : String(saved));
  }, [saved]);

  const myRole = members?.find((m) => m.user.id === me?.id)?.role;
  const canEdit =
    !!me?.isGlobalAdmin ||
    (onboarding?.step === "ready" && onboarding.organizationRole === "admin") ||
    myRole === "admin";

  const isBlank = draft.trim() === "";
  const parsed = parseThreshold(draft);
  const invalid = !isBlank && parsed === null;
  const next = isBlank ? null : parsed;
  const changed = !invalid && next !== saved;

  function save() {
    if (!changed) return;
    setThreshold.mutate(next);
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="flex items-center gap-2 text-lg font-medium">
          <ShieldCheck className="h-4 w-4" />
          Minimum compliance score
        </h3>
        <p className="text-sm text-muted-foreground">
          A run can only start when the epic scores at least this much. Leave it empty to use the
          default ({DEFAULT_COMPLIANCE_THRESHOLD}).
        </p>
      </div>

      <form
        className="flex items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <label className="space-y-1 text-sm">
          <span className="block font-medium">Score (50-100)</span>
          <Input
            type="number"
            inputMode="numeric"
            min={MIN}
            max={MAX}
            step={1}
            className="w-32"
            placeholder={String(DEFAULT_COMPLIANCE_THRESHOLD)}
            value={draft}
            disabled={!canEdit || setThreshold.isPending}
            aria-invalid={invalid}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
          />
        </label>
        {canEdit ? (
          <Button type="submit" disabled={!changed || setThreshold.isPending}>
            {setThreshold.isPending ? "Saving..." : "Save"}
          </Button>
        ) : null}
      </form>

      {invalid ? (
        <p className="text-xs text-destructive">Enter a whole number from 50 to 100, or leave it empty.</p>
      ) : null}
      {!canEdit ? (
        <p className="text-xs text-muted-foreground">Only a project admin can change this.</p>
      ) : null}
      {errorText(setThreshold.error) ? (
        <p className="text-xs text-destructive">{errorText(setThreshold.error)}</p>
      ) : null}
    </div>
  );
}
